import {
  CSRF_HEADER,
  hasDerivedOrigin,
  type PrincipalOptions,
  type ProjectDocument,
  type ProjectMemberView,
  type ProjectView,
  type Reader,
  type UpdateDocumentRequest,
} from "@esi/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AppDialog } from "@/components/AppDialog";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { api } from "../api.ts";
import { ApiError, apiError } from "../api-errors.ts";
import { t as translate } from "../i18n/i18n.ts";
import { useSession } from "../session.tsx";
import { useUnsavedWork } from "../shell/session-ended.ts";
import { GeneratedMark } from "../trust/GeneratedMark.tsx";
import {
  AccessFields,
  type AccessState,
  accessOf,
  accessReaders,
  nothingPicked,
} from "./AccessFields.tsx";
import { type AccessDiff, diffAccess, isGeneratedDocument, sameReaders } from "./file-actions.ts";
import { type KnowledgeSettings, projectKeys } from "./queries.ts";
import { shortId } from "./readers.ts";

/** The server's refusal of a file change, in plain words (known reasons translated). */
export function documentRefusal(error: Error): string {
  const message = error.message;
  if (error instanceof ApiError && error.code === "project_frozen") {
    return translate("files.access.refused.frozen");
  }
  if (/readers must be able to read every source/i.test(message)) {
    return translate("files.access.refused.derived");
  }
  if (/classified below a source/i.test(message)) {
    return translate("files.access.refused.belowSource");
  }
  if (/declassified content/i.test(message)) {
    return translate("files.access.refused.declassified");
  }
  return message;
}

type Project = ProjectView & { ownerName?: string | null };

/**
 * Changes who can read a file, its classification and origin. Only changed fields are sent.
 * A change that lets more people read it, or download it, first shows who gains access (by
 * names the viewer can already see) and is sent only once confirmed.
 */
export function EditAccessDialog(props: {
  project: Project;
  document: ProjectDocument | null;
  settings: KnowledgeSettings;
  members: readonly ProjectMemberView[] | undefined;
  principals: PrincipalOptions | undefined;
  onClose: () => void;
  /** The row's actions button, focused again on close. */
  returnFocus?: HTMLElement | null;
}) {
  const { t } = useTranslation();
  return (
    <AppDialog
      returnFocus={props.returnFocus}
      open={props.document !== null}
      onClose={props.onClose}
      title={props.document ? t("files.access.title", { title: props.document.title }) : ""}
      description={t("files.access.lead")}
    >
      {props.document ? <EditAccessForm {...props} document={props.document} /> : null}
    </AppDialog>
  );
}

function EditAccessForm(props: {
  project: Project;
  document: ProjectDocument;
  settings: KnowledgeSettings;
  members: readonly ProjectMemberView[] | undefined;
  principals: PrincipalOptions | undefined;
  onClose: () => void;
}) {
  const { project, document: d, settings, members, principals, onClose } = props;
  const { t } = useTranslation();
  const { csrfToken, userId } = useSession();
  const queryClient = useQueryClient();
  const initial = accessOf(d);
  const [access, setAccess] = useState<AccessState>(initial);
  const [confirming, setConfirming] = useState<AccessDiff | null>(null);
  const [unchanged, setUnchanged] = useState(false);
  // An open edit (or one awaiting its widening confirmation) would be lost by a page change.
  useUnsavedWork(true);
  const uploader = members?.find((m) => m.userId.toLowerCase() === d.uploadedBy.toLowerCase());
  const fixedLabel =
    d.uploadedBy.toLowerCase() === userId.toLowerCase()
      ? t("upload.you")
      : t("files.access.uploaderIncluded", {
          name: uploader?.displayName ?? shortId(d.uploadedBy),
        });

  // Readers are sent only when the person changed them: an untouched reader set (even one the
  // form cannot express exactly) is left as it is.
  const patch = (): UpdateDocumentRequest => {
    // Compared as form state: a stored reader set the form cannot rebuild for this project (an
    // entity reader on a personal project) is kept as it is unless the person changes it.
    const touched = readerChoice(access) !== readerChoice(initial);
    const readers = touched ? accessReaders(access, project, d.uploadedBy) : null;
    return {
      ...(readers && !sameReaders(d.readers, readers) ? { readers } : {}),
      ...(access.classification !== d.classification
        ? { classification: access.classification }
        : {}),
      // Against the form's own start: a `generated` origin is not rewritten by an unrelated edit.
      ...(access.origin !== initial.origin ? { origin: access.origin } : {}),
    };
  };

  const save = useMutation({
    mutationFn: async (body: UpdateDocumentRequest) => {
      const res = await api.api.projects[":projectId"].documents[":documentId"].$patch(
        { param: { projectId: project.id, documentId: d.id }, json: body },
        { headers: { [CSRF_HEADER]: csrfToken } },
      );
      if (!res.ok) {
        throw await apiError(res);
      }
      return res.status;
    },
    onSuccess: (status) => {
      // 204: the change took the caller out of the readers; the file leaves their list.
      toast(
        status === 204
          ? t("files.access.narrowedOut", { title: d.title })
          : t("files.access.updated", { title: d.title }),
      );
      onClose();
    },
    onError: () => setConfirming(null),
    onSettled: () => queryClient.invalidateQueries({ queryKey: projectKeys.documents(project.id) }),
  });

  function submit() {
    const body = patch();
    if (Object.keys(body).length === 0) {
      setUnchanged(true);
      return;
    }
    const diff = diffAccess(
      d,
      {
        readers: body.readers ?? d.readers,
        classification: body.classification ?? d.classification,
        origin: d.origin,
      },
      settings.scheme,
    );
    if (diff.widens && !confirming) {
      setConfirming(diff);
      return;
    }
    save.mutate(body);
  }

  const busy = save.isPending;
  if (confirming) {
    const level = settings.scheme.find((l) => l.id === access.classification);
    return (
      <form
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-medium text-fg">{t("files.access.widenTitle")}</h3>
          <p className="text-sm text-fg-secondary">
            {t("files.access.widenLead", { title: d.title })}
          </p>
          <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-fg">
            {confirming.added.map((r) => (
              <li key={`${r.kind}:${r.ref}`}>{gainLabel(r, project, members, principals)}</li>
            ))}
            {confirming.downloadUnlocked ? <li>{t("files.access.downloadUnlocked")}</li> : null}
            {confirming.floorWidened && level ? (
              <li>{t("files.access.floorWidened", { level: level.label })}</li>
            ) : null}
          </ul>
        </div>
        {save.error ? <Notice tone="danger">{documentRefusal(save.error)}</Notice> : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => setConfirming(null)}
          >
            {t("files.access.back")}
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? t("files.access.saving") : t("files.access.confirmWiden")}
          </Button>
        </DialogFooter>
      </form>
    );
  }

  return (
    <form
      className="flex flex-col gap-6"
      aria-busy={busy}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <AccessFields
        project={project}
        settings={settings}
        fixedOrigin={
          hasDerivedOrigin(d) ? (
            isGeneratedDocument(d) ? (
              <GeneratedMark className="text-sm" />
            ) : (
              t(
                d.origin === "external"
                  ? "files.originLabel.external"
                  : "files.originLabel.internal",
              )
            )
          ) : undefined
        }
        members={members}
        principals={principals}
        value={access}
        onChange={(next) => {
          setUnchanged(false);
          save.reset();
          setAccess(next);
        }}
        disabled={busy}
        fixedReader={{ id: d.uploadedBy, label: fixedLabel }}
      />
      {save.error ? <Notice tone="danger">{documentRefusal(save.error)}</Notice> : null}
      {unchanged ? <p className="text-sm text-fg-secondary">{t("files.access.noChange")}</p> : null}
      <DialogFooter>
        <Button type="button" variant="outline" disabled={busy} onClick={onClose}>
          {t("files.cancel")}
        </Button>
        <Button type="submit" disabled={busy || nothingPicked(access)}>
          {busy ? t("files.access.saving") : t("files.access.save")}
        </Button>
      </DialogFooter>
    </form>
  );
}

/** The reader part of the form, comparable as a string. */
const readerChoice = (state: AccessState) =>
  JSON.stringify([
    state.audience,
    [...state.named].sort(),
    [...state.groups].sort(),
    [...state.rules].sort(),
  ]);

/** Who a new reader lets in, by names the viewer already sees; never a list of people. */
function gainLabel(
  reader: Reader,
  project: Project,
  members: readonly ProjectMemberView[] | undefined,
  principals: PrincipalOptions | undefined,
): string {
  const ref = reader.ref.toLowerCase();
  const owner =
    project.owner.kind === "entity" && project.owner.entityId.toLowerCase() === ref
      ? (project.ownerName ?? translate("files.access.gains.ownerFallback"))
      : null;
  switch (reader.kind) {
    case "project":
      return members
        ? translate("files.access.gains.project", { count: members.length })
        : translate("files.access.gains.projectUnknown");
    case "entity":
      return translate("files.access.gains.entity", {
        entity: owner ?? translate("readers.entityRef", { ref: shortId(reader.ref) }),
      });
    case "entity_tree":
      return translate("files.access.gains.entityTree", {
        entity: owner ?? translate("readers.entityRef", { ref: shortId(reader.ref) }),
      });
    case "user": {
      const member = members?.find((m) => m.userId.toLowerCase() === ref);
      return member?.displayName ?? translate("files.access.gains.unnamed");
    }
    case "group": {
      const group = principals?.groups.find((g) => g.id.toLowerCase() === ref);
      return group
        ? translate("files.access.gains.group", { name: group.displayName })
        : translate("files.access.gains.unnamed");
    }
    case "attribute_rule": {
      const rule = principals?.rules.find((r) => r.id.toLowerCase() === ref);
      return rule
        ? translate("files.access.gains.rule", { name: rule.name })
        : translate("files.access.gains.unnamed");
    }
    default:
      return translate("files.access.gains.unnamed");
  }
}
